import React, { useEffect, useState } from 'react';
import { useCompliance } from '../../contexts/ComplianceContext';
import IntegrationCard from '../../components/compliance/IntegrationCard';
import RemoveInstallationModal from '../../components/RemoveInstallationModal';
import { ArrowRight, AlertTriangle, RefreshCw, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const ConnectIntegrationsStep = () => {
  const {
    integrations,
    fetchIntegrationStatus,
    fetchGitHubAppInfo,
    connectAWS,
    connectAWSSSO,
    cancelAWSSSO,
    selectAWSSSORole,
    configureAWSRole,
    awsSSOState,
    connectGoogleWorkspace,
    connectGitHub,
    removeGithubInstallation,
    githubAppInfo,
    proceedToScan,
    loading,
  } = useCompliance();

  const [refreshing, setRefreshing] = useState(false);
  const [skippedIntegrations, setSkippedIntegrations] = useState([]);
  const [showRemoveGithubModal, setShowRemoveGithubModal] = useState(false);
  const [removeGithubLoading, setRemoveGithubLoading] = useState(false);
  const [removeGithubError, setRemoveGithubError] = useState(null);
  const [removeTargetInstallation, setRemoveTargetInstallation] = useState(null);

  // Fetch integration status and GitHub App info on mount
  useEffect(() => {
    fetchIntegrationStatus();
    fetchGitHubAppInfo();
  }, [fetchIntegrationStatus, fetchGitHubAppInfo]);

  // Refresh integration status
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchIntegrationStatus(),
        fetchGitHubAppInfo(),
      ]);
      toast.success('Integration status refreshed');
    } catch (err) {
      toast.error('Failed to refresh status');
    } finally {
      setRefreshing(false);
    }
  };

  // Handle skip integration
  const handleSkip = (integrationType) => {
    if (!skippedIntegrations.includes(integrationType)) {
      setSkippedIntegrations([...skippedIntegrations, integrationType]);
      toast('Integration skipped - manual verification required', {
        icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
      });
    }
  };

  // Handle opening the remove modal for a specific installation
  const handleOpenRemoveModal = () => {
    const installations = githubAppInfo?.installations || [];
    if (installations.length === 0) return;
    // If only one installation, target it directly; otherwise let user pick via the modal
    setRemoveTargetInstallation(installations.length === 1 ? installations[0] : null);
    setShowRemoveGithubModal(true);
  };

  // Handle remove GitHub App installation
  const handleRemoveGithubInstallation = async (installation) => {
    const target = installation || removeTargetInstallation;
    if (!target) return;
    setRemoveGithubLoading(true);
    setRemoveGithubError(null);
    try {
      const result = await removeGithubInstallation(target.id);
      if (result.success) {
        setShowRemoveGithubModal(false);
        setRemoveTargetInstallation(null);
        toast.success('GitHub App installation removed');
      } else {
        setRemoveGithubError(result.error);
      }
    } catch (err) {
      setRemoveGithubError(err.message || 'Failed to remove installation');
    } finally {
      setRemoveGithubLoading(false);
    }
  };

  // Count connected integrations (GitHub counts as connected if App is installed)
  const isGitHubReady = githubAppInfo?.installations?.length > 0;
  const connectedCount = Object.entries(integrations).filter(([key, i]) => {
    if (key === 'github') return i.connected || isGitHubReady;
    return i.connected;
  }).length;
  const totalCount = Object.keys(integrations).length;

  // Check if user can proceed (at least one connected or all skipped)
  const canProceed = connectedCount > 0 || skippedIntegrations.length === totalCount;

  // Handle proceed to scan
  const handleProceed = () => {
    if (!canProceed) {
      toast.error('Please connect at least one integration or skip all to proceed');
      return;
    }
    proceedToScan();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Connect Your Integrations</h2>
          <p className="text-sm text-gray-500 mt-1">
            Connect your cloud services to enable automated compliance verification
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="flex flex-shrink-0 items-center gap-2 px-3 py-2 min-h-[44px] text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Status
        </button>
      </div>

      {/* Connection Status Summary */}
      <div className="bg-gray-100 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-gray-600">
            {connectedCount} of {totalCount} integrations connected
          </span>
          <div className="flex gap-1">
            {Object.entries(integrations).map(([key, value]) => {
              const isConnected = key === 'github'
                ? (value.connected || isGitHubReady)
                : value.connected;
              return (
                <div
                  key={key}
                  className={`w-3 h-3 rounded-full ${
                    isConnected ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  title={`${key}: ${isConnected ? 'Connected' : 'Not connected'}`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Integration Cards. GitHub is first: it gates `isGitHubReady` below, and
          on a phone anything below the first card is off-screen. */}
      <div className="space-y-4">
        <IntegrationCard
          type="github"
          connected={integrations.github.connected}
          status={integrations.github.status}
          integrationName={integrations.github.name}
          onConnect={connectGitHub}
          onSkip={() => handleSkip('github')}
          disabled={loading}
          githubAppInfo={githubAppInfo}
          onRemoveInstallation={handleOpenRemoveModal}
        />

        <IntegrationCard
          type="aws"
          connected={integrations.aws.connected}
          status={integrations.aws.status}
          integrationName={integrations.aws.name}
          integrationId={integrations.aws.integrationId}
          onConnect={connectAWS}
          onConnectSSO={connectAWSSSO}
          onSkip={() => handleSkip('aws')}
          disabled={loading}
          ssoState={awsSSOState.state}
          ssoVerificationUrl={awsSSOState.verificationUrlComplete || awsSSOState.verificationUrl}
          ssoUserCode={awsSSOState.userCode}
          ssoStatus={awsSSOState.status}
          ssoMessage={awsSSOState.message}
          onCancelSSO={cancelAWSSSO}
          ssoShowRoleSelection={awsSSOState.showRoleSelection}
          ssoAccounts={awsSSOState.accounts}
          ssoAccountsLoading={awsSSOState.accountsLoading}
          ssoAccountsError={awsSSOState.accountsError}
          onSelectSSORole={selectAWSSSORole}
          onConfigureRole={configureAWSRole}
        />

        <IntegrationCard
          type="google_workspace"
          connected={integrations.google_workspace.connected}
          status={integrations.google_workspace.status}
          integrationName={integrations.google_workspace.name}
          onConnect={connectGoogleWorkspace}
          onSkip={() => handleSkip('google_workspace')}
          disabled={loading}
        />
      </div>

      {/* Skip All Warning */}
      {connectedCount === 0 && skippedIntegrations.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Limited Automation</p>
            <p className="text-sm text-amber-700 mt-1">
              Without integrations, most compliance controls will require manual evidence collection and verification.
              You can connect integrations at any time to enable automated checks.
            </p>
          </div>
        </div>
      )}

      {/* Navigation Buttons. flex-col-reverse keeps the primary CTA above
          "Skip all" on mobile. */}
      <div className="flex flex-col-reverse gap-3 pt-4 border-t border-gray-200 sm:flex-row sm:justify-between">
        <button
          onClick={() => {
            // Skip all remaining integrations
            const notConnected = Object.keys(integrations).filter(
              key => !integrations[key].connected && !skippedIntegrations.includes(key)
            );
            setSkippedIntegrations([...skippedIntegrations, ...notConnected]);
            proceedToScan();
          }}
          className="px-4 py-2 min-h-[44px] text-sm text-gray-500 hover:text-gray-700 font-medium"
        >
          Skip all and continue
        </button>

        <button
          onClick={handleProceed}
          disabled={loading || !canProceed}
          className={`
            flex w-full sm:w-auto justify-center items-center gap-2 px-6 py-3 min-h-[44px]
            bg-teal-600 text-white font-medium rounded-lg
            hover:bg-teal-700 transition-colors
            ${(loading || !canProceed) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {loading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              Continue to Scan
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>

      {showRemoveGithubModal && removeTargetInstallation && (
        <RemoveInstallationModal
          isOpen={true}
          accountLogin={removeTargetInstallation.account?.login || ''}
          onConfirm={() => handleRemoveGithubInstallation(removeTargetInstallation)}
          onClose={() => { setShowRemoveGithubModal(false); setRemoveTargetInstallation(null); setRemoveGithubError(null); }}
          isLoading={removeGithubLoading}
          error={removeGithubError}
        />
      )}

      {showRemoveGithubModal && !removeTargetInstallation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Select Installation to Remove</h3>
              <button
                onClick={() => { setShowRemoveGithubModal(false); setRemoveGithubError(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-xl">&times;</span>
              </button>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-sm text-gray-600 mb-3">Select which GitHub App installation to remove:</p>
              {(githubAppInfo?.installations || []).map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => {
                    setRemoveTargetInstallation(inst);
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{inst.account?.login}</span>
                  <span className="text-sm text-gray-500 ml-2">({inst.account?.type})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectIntegrationsStep;
